import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Merge, 
  RefreshCw, 
  Users, 
  Building2, 
  MapPin, 
  AlertTriangle,
  CheckCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Shield,
  Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DuplicateMergeCard } from '../components/DuplicateWarningPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DataMergeDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [integrityResult, setIntegrityResult] = useState(null);
  const [mergeLogs, setMergeLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('contractors');
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [merging, setMerging] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadMergeLogs();
    loadIntegrityCheck();
  }, []);

  const loadMergeLogs = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/data-merge/logs?limit=20`, { headers });
      setMergeLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to load merge logs:', err);
    }
  };

  const loadIntegrityCheck = async () => {
    try {
      const res = await axios.get(`${API}/api/data-audit/integrity-check`, { headers });
      setIntegrityResult(res.data);
    } catch (err) {
      console.error('Failed to load integrity check:', err);
    }
  };

  const runDuplicateScan = async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/api/admin/data-merge/scan`, { headers });
      setScanResult(res.data);
      toast.success('Duplicate scan completed');
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error('Failed to scan for duplicates');
    } finally {
      setScanning(false);
    }
  };

  const handleMerge = async (sourceId, targetId) => {
    setMerging(true);
    try {
      await axios.post(
        `${API}/api/admin/data-merge/${activeTab}/merge`,
        { source_id: sourceId, target_id: targetId },
        { headers }
      );
      toast.success('Records merged successfully');
      setSelectedDuplicate(null);
      // Refresh scan
      runDuplicateScan();
      loadMergeLogs();
    } catch (err) {
      console.error('Merge failed:', err);
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'contractors': return <Users className="w-5 h-5" />;
      case 'clients': return <Building2 className="w-5 h-5" />;
      case 'parks': return <MapPin className="w-5 h-5" />;
      default: return <Users className="w-5 h-5" />;
    }
  };

  const getDuplicates = () => {
    if (!scanResult) return [];
    return scanResult[activeTab]?.duplicates || [];
  };

  const getCount = (type) => {
    if (!scanResult) return 0;
    return scanResult[type]?.count || 0;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <Shield className="w-7 h-7 text-teal-600" />
                Data Enforcement Dashboard
              </h1>
              <p className="text-slate-500 mt-1">
                Manage duplicates, enforce data quality, and maintain system integrity
              </p>
            </div>
            <button
              onClick={runDuplicateScan}
              disabled={scanning}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
              {scanning ? 'Scanning...' : 'Run Duplicate Scan'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Contractors */}
          <button
            onClick={() => setActiveTab('contractors')}
            className={`p-4 rounded-xl border ${
              activeTab === 'contractors' 
                ? 'bg-teal-50 border-teal-200' 
                : 'bg-white border-slate-200 hover:border-teal-200'
            } transition-colors text-left`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeTab === 'contractors' ? 'bg-teal-100' : 'bg-slate-100'}`}>
                <Users className={`w-5 h-5 ${activeTab === 'contractors' ? 'text-teal-600' : 'text-slate-600'}`} />
              </div>
              <span className="font-semibold text-slate-800">Contractors</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{getCount('contractors')}</div>
            <div className="text-sm text-slate-500">duplicate pairs</div>
          </button>

          {/* Clients */}
          <button
            onClick={() => setActiveTab('clients')}
            className={`p-4 rounded-xl border ${
              activeTab === 'clients' 
                ? 'bg-teal-50 border-teal-200' 
                : 'bg-white border-slate-200 hover:border-teal-200'
            } transition-colors text-left`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeTab === 'clients' ? 'bg-teal-100' : 'bg-slate-100'}`}>
                <Building2 className={`w-5 h-5 ${activeTab === 'clients' ? 'text-teal-600' : 'text-slate-600'}`} />
              </div>
              <span className="font-semibold text-slate-800">Clients</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{getCount('clients')}</div>
            <div className="text-sm text-slate-500">duplicate pairs</div>
          </button>

          {/* Parks */}
          <button
            onClick={() => setActiveTab('parks')}
            className={`p-4 rounded-xl border ${
              activeTab === 'parks' 
                ? 'bg-teal-50 border-teal-200' 
                : 'bg-white border-slate-200 hover:border-teal-200'
            } transition-colors text-left`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${activeTab === 'parks' ? 'bg-teal-100' : 'bg-slate-100'}`}>
                <MapPin className={`w-5 h-5 ${activeTab === 'parks' ? 'text-teal-600' : 'text-slate-600'}`} />
              </div>
              <span className="font-semibold text-slate-800">Parks</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{getCount('parks')}</div>
            <div className="text-sm text-slate-500">duplicate pairs</div>
          </button>

          {/* Integrity Status */}
          <div className={`p-4 rounded-xl border ${
            integrityResult?.status === 'healthy' 
              ? 'bg-emerald-50 border-emerald-200' 
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${
                integrityResult?.status === 'healthy' ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {integrityResult?.status === 'healthy' ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
              </div>
              <span className="font-semibold text-slate-800">Integrity</span>
            </div>
            <div className="text-2xl font-bold text-slate-900">{integrityResult?.total_issues || 0}</div>
            <div className="text-sm text-slate-500">issues found</div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Duplicates List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                  {getEntityIcon(activeTab)}
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Duplicates
                </h2>
                <span className="text-sm text-slate-500">
                  {getDuplicates().length} pairs
                </span>
              </div>

              {!scanResult ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Run a scan to detect duplicates</p>
                  <button
                    onClick={runDuplicateScan}
                    className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
                  >
                    Start Scan
                  </button>
                </div>
              ) : getDuplicates().length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <p className="text-slate-500">No duplicates found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {getDuplicates().map((dup, idx) => (
                    <div
                      key={idx}
                      className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                        selectedDuplicate === idx ? 'bg-teal-50' : ''
                      }`}
                      onClick={() => setSelectedDuplicate(selectedDuplicate === idx ? null : idx)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-800">
                              {dup.record_a?.company_name || dup.record_a?.name || 'Record A'}
                            </span>
                            <span className="text-slate-400">↔</span>
                            <span className="font-medium text-slate-800">
                              {dup.record_b?.company_name || dup.record_b?.name || 'Record B'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              dup.match_score >= 90 ? 'bg-red-100 text-red-700' :
                              dup.match_score >= 70 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {dup.match_score}% match
                            </span>
                            {dup.match_reasons?.map((reason, i) => (
                              <span key={i} className="text-slate-500 text-xs">
                                {reason.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                          selectedDuplicate === idx ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Selected Duplicate or Logs */}
          <div className="space-y-6">
            {/* Selected Duplicate Card */}
            {selectedDuplicate !== null && getDuplicates()[selectedDuplicate] && (
              <DuplicateMergeCard
                recordA={getDuplicates()[selectedDuplicate].record_a}
                recordB={getDuplicates()[selectedDuplicate].record_b}
                entityType={activeTab.slice(0, -1)} // Remove 's' from end
                matchScore={getDuplicates()[selectedDuplicate].match_score}
                matchReasons={getDuplicates()[selectedDuplicate].match_reasons}
                onMerge={handleMerge}
                onDismiss={() => setSelectedDuplicate(null)}
              />
            )}

            {/* Integrity Issues */}
            {integrityResult?.issues?.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Integrity Issues
                  </h3>
                </div>
                <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                  {integrityResult.issues.map((issue, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          issue.severity === 'high' ? 'bg-red-100 text-red-700' :
                          issue.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {issue.severity}
                        </span>
                        <span className="text-sm font-medium text-slate-800">{issue.type.replace(/_/g, ' ')}</span>
                      </div>
                      <p className="text-sm text-slate-600">{issue.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Merge Logs */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-slate-500" />
                  Recent Merges
                </h3>
              </div>
              {mergeLogs.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  No merge history yet
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[200px] overflow-y-auto">
                  {mergeLogs.slice(0, 5).map((log, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-teal-600 uppercase">
                          {log.entity_type}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(log.merged_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Merged by {log.merged_by}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
